import React, { useState } from 'react';
import { FactCheck, Source } from '../data/mockData';
import { CheckCircleIcon, AlertCircleIcon, AlertTriangleIcon, HelpCircleIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, ClockIcon, XIcon, FilterIcon } from 'lucide-react';
interface DetailedTimelineViewProps {
  factChecks: FactCheck[];
  currentTime: number;
  onSeek: (time: number) => void;
}
export const DetailedTimelineView: React.FC<DetailedTimelineViewProps> = ({
  factChecks,
  currentTime,
  onSeek
}) => {
  const [expandedFactCheck, setExpandedFactCheck] = useState<number | null>(null);
  const [filters, setFilters] = useState<{
    rating: string | null;
    speaker: string | null;
    category: string | null;
  }>({
    rating: null,
    speaker: null,
    category: null
  });
  // Calculate statistics
  const totalFactChecks = factChecks.length;
  const trueFactChecks = factChecks.filter(check => check.rating === 'True').length;
  const falseFactChecks = factChecks.filter(check => check.rating === 'False').length;
  const misleadingFactChecks = factChecks.filter(check => check.rating === 'Misleading').length;
  const unverifiedFactChecks = factChecks.filter(check => check.rating === 'Unverified').length;
  // Get unique speakers and count their claims
  const speakerStats = factChecks.reduce((acc, check) => {
    if (!acc[check.speaker]) {
      acc[check.speaker] = {
        total: 0,
        true: 0,
        false: 0,
        misleading: 0,
        unverified: 0
      };
    }
    acc[check.speaker].total++;
    if (check.rating === 'True') acc[check.speaker].true++;
    if (check.rating === 'False') acc[check.speaker].false++;
    if (check.rating === 'Misleading') acc[check.speaker].misleading++;
    if (check.rating === 'Unverified') acc[check.speaker].unverified++;
    return acc;
  }, {} as Record<string, {
    total: number;
    true: number;
    false: number;
    misleading: number;
    unverified: number;
  }>);
  // Get unique categories
  const categories = [...new Set(factChecks.map(check => check.category))].filter(Boolean) as string[];
  // Filter fact checks based on active filters
  const filteredFactChecks = factChecks.filter(check => {
    if (filters.rating && check.rating !== filters.rating) return false;
    if (filters.speaker && check.speaker !== filters.speaker) return false;
    if (filters.category && check.category !== filters.category) return false;
    return true;
  });
  // Format time (MM:SS)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // Get icon based on rating
  const getRatingIcon = (rating: string) => {
    switch (rating) {
      case 'True':
        return <CheckCircleIcon className="text-green-500" size={20} data-id="element-23" />;
      case 'False':
        return <AlertCircleIcon className="text-red-500" size={20} data-id="element-24" />;
      case 'Misleading':
        return <AlertTriangleIcon className="text-yellow-500" size={20} data-id="element-25" />;
      default:
        return <HelpCircleIcon className="text-gray-500" size={20} data-id="element-26" />;
    }
  };
  // Get background color based on rating
  const getRatingClass = (rating: string): string => {
    switch (rating) {
      case 'True':
        return 'bg-green-50 border-green-200';
      case 'False':
        return 'bg-red-50 border-red-200';
      case 'Misleading':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };
  // Get badge color based on rating
  const getRatingBadgeClass = (rating: string): string => {
    switch (rating) {
      case 'True':
        return 'bg-green-100 text-green-800';
      case 'False':
        return 'bg-red-100 text-red-800';
      case 'Misleading':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Handle click on a fact check
  const handleFactCheckClick = (index: number, timestamp: number) => {
    setExpandedFactCheck(expandedFactCheck === index ? null : index);
    onSeek(timestamp);
  };
  // Handle filter by rating
  const handleFilterByRating = (rating: string) => {
    setFilters(prev => ({
      ...prev,
      rating: prev.rating === rating ? null : rating
    }));
    setExpandedFactCheck(null);
  };
  // Handle filter by speaker
  const handleFilterBySpeaker = (speaker: string) => {
    setFilters(prev => ({
      ...prev,
      speaker: prev.speaker === speaker ? null : speaker
    }));
    setExpandedFactCheck(null);
  };
  // Handle filter by category
  const handleFilterByCategory = (category: string) => {
    setFilters(prev => ({
      ...prev,
      category: prev.category === category ? null : category
    }));
    setExpandedFactCheck(null);
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      rating: null,
      speaker: null,
      category: null
    });
  };
  // Render source links
  const renderSources = (sources: Source[]) => {
    return <div className="mt-3 space-y-2" data-id="element-27">
        <h4 className="font-medium text-sm text-gray-700" data-id="element-28">Sources:</h4>
        <ul className="space-y-2 text-sm" data-id="element-29">
          {sources.map((source, index) => <li key={index} className="border-l-2 border-gray-300 pl-3" data-id="element-30">
              <div className="font-medium" data-id="element-31">{source.name}</div>
              {source.date && <div className="text-xs text-gray-500" data-id="element-32">Date: {source.date}</div>}
              {source.quote && <div className="text-gray-600 italic mt-1" data-id="element-33">
                  "{source.quote}"
                </div>}
              {source.url && <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center mt-1" data-id="element-34">
                  View Source <ExternalLinkIcon size={14} className="ml-1" data-id="element-35" />
                </a>}
            </li>)}
        </ul>
      </div>;
  };
  // Check if any filters are active
  const hasActiveFilters = filters.rating || filters.speaker || filters.category;
  return <div className="space-y-6" data-id="element-36">
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-37">
        <h3 className="text-lg font-medium mb-3" data-id="element-38">Fact Check Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4" data-id="element-39">
          <button onClick={() => clearFilters()} className={`p-3 rounded-md text-left transition-all ${!hasActiveFilters ? 'bg-blue-100 ring-2 ring-blue-300' : 'bg-blue-50 hover:bg-blue-100'}`} data-id="element-40">
            <div className="text-sm text-blue-700" data-id="element-41">Total Fact Checks</div>
            <div className="text-2xl font-bold" data-id="element-42">{totalFactChecks}</div>
          </button>
          <button onClick={() => handleFilterByRating('True')} className={`p-3 rounded-md text-left transition-all ${filters.rating === 'True' ? 'bg-green-100 ring-2 ring-green-300' : 'bg-green-50 hover:bg-green-100'}`} data-id="element-43">
            <div className="text-sm text-green-700" data-id="element-44">True Claims</div>
            <div className="text-2xl font-bold" data-id="element-45">
              {trueFactChecks}{' '}
              <span className="text-sm font-normal" data-id="element-46">
                ({Math.round(trueFactChecks / totalFactChecks * 100)}%)
              </span>
            </div>
          </button>
          <button onClick={() => handleFilterByRating('False')} className={`p-3 rounded-md text-left transition-all ${filters.rating === 'False' ? 'bg-red-100 ring-2 ring-red-300' : 'bg-red-50 hover:bg-red-100'}`} data-id="element-47">
            <div className="text-sm text-red-700" data-id="element-48">False Claims</div>
            <div className="text-2xl font-bold" data-id="element-49">
              {falseFactChecks}{' '}
              <span className="text-sm font-normal" data-id="element-50">
                ({Math.round(falseFactChecks / totalFactChecks * 100)}%)
              </span>
            </div>
          </button>
          <button onClick={() => handleFilterByRating('Misleading')} className={`p-3 rounded-md text-left transition-all ${filters.rating === 'Misleading' ? 'bg-yellow-100 ring-2 ring-yellow-300' : 'bg-yellow-50 hover:bg-yellow-100'}`} data-id="element-51">
            <div className="text-sm text-yellow-700" data-id="element-52">Misleading Claims</div>
            <div className="text-2xl font-bold" data-id="element-53">
              {misleadingFactChecks}{' '}
              <span className="text-sm font-normal" data-id="element-54">
                ({Math.round(misleadingFactChecks / totalFactChecks * 100)}%)
              </span>
            </div>
          </button>
        </div>
        {hasActiveFilters && <div className="flex items-center mb-4 p-2 bg-blue-50 rounded-md" data-id="element-55">
            <FilterIcon size={16} className="text-blue-500 mr-2" data-id="element-56" />
            <span className="text-blue-700 text-sm" data-id="element-57">
              Filtering by:
              {filters.rating && <span className="ml-1 font-medium" data-id="element-58">{filters.rating}</span>}
              {filters.speaker && <span className="ml-1 font-medium" data-id="element-59">
                  {filters.rating ? ' + ' : ''}
                  {filters.speaker}
                </span>}
              {filters.category && <span className="ml-1 font-medium" data-id="element-60">
                  {filters.rating || filters.speaker ? ' + ' : ''}
                  {filters.category}
                </span>}
            </span>
            <button onClick={clearFilters} className="ml-auto text-blue-700 hover:text-blue-800 flex items-center" aria-label="Clear filters" data-id="element-61">
              <XIcon size={16} data-id="element-62" />
              <span className="ml-1 text-sm" data-id="element-63">Clear</span>
            </button>
          </div>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-64">
          <div data-id="element-65">
            <h4 className="font-medium mb-2 text-gray-700" data-id="element-66">By Speaker</h4>
            <div className="space-y-2" data-id="element-67">
              {Object.entries(speakerStats).map(([speaker, stats]) => <button key={speaker} onClick={() => handleFilterBySpeaker(speaker)} className={`w-full p-2 rounded-md text-left transition-all ${filters.speaker === speaker ? 'bg-blue-100 ring-2 ring-blue-300' : 'bg-gray-50 hover:bg-gray-100'}`} data-id="element-68">
                  <div className="font-medium" data-id="element-69">{speaker}</div>
                  <div className="flex items-center space-x-2 mt-1 text-sm" data-id="element-70">
                    <span className="text-gray-600" data-id="element-71">Total: {stats.total}</span>
                    <span className="text-green-600" data-id="element-72">True: {stats.true}</span>
                    <span className="text-red-600" data-id="element-73">False: {stats.false}</span>
                    <span className="text-yellow-600" data-id="element-74">
                      Misleading: {stats.misleading}
                    </span>
                  </div>
                </button>)}
            </div>
          </div>
          <div data-id="element-75">
            <h4 className="font-medium mb-2 text-gray-700" data-id="element-76">By Category</h4>
            <div className="flex flex-wrap gap-2" data-id="element-77">
              {categories.map(category => {
              const count = factChecks.filter(check => check.category === category).length;
              return <button key={category} onClick={() => handleFilterByCategory(category)} className={`px-3 py-2 rounded-full text-sm transition-all ${filters.category === category ? 'bg-blue-200 text-blue-800' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`} data-id="element-78">
                    {category}: <span className="font-medium" data-id="element-79">{count}</span>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-id="element-80">
        <div className="flex justify-between items-center mb-4" data-id="element-81">
          <h3 className="text-lg font-medium" data-id="element-82">
            {hasActiveFilters ? 'Filtered Timeline' : 'Detailed Timeline'}
          </h3>
          <div className="text-sm text-gray-500" data-id="element-83">
            Showing {filteredFactChecks.length} of {totalFactChecks} fact checks
          </div>
        </div>
        {filteredFactChecks.length === 0 ? <div className="py-10 text-center text-gray-500" data-id="element-84">
            <div className="mb-2" data-id="element-85">
              No fact checks match your current filters
            </div>
            <button onClick={clearFilters} className="text-blue-600 hover:underline" data-id="element-86">
              Clear all filters
            </button>
          </div> : <div className="space-y-3" data-id="element-87">
            {filteredFactChecks.sort((a, b) => a.timestamp - b.timestamp).map((check, index) => <div key={index} className={`border rounded-md transition-all ${getRatingClass(check.rating)} ${currentTime >= check.timestamp && currentTime <= check.timestamp + 5 ? 'ring-2 ring-blue-400' : ''}`} data-id="element-88">
                  <div className="p-4 cursor-pointer" onClick={() => handleFactCheckClick(index, check.timestamp)} data-id="element-89">
                    <div className="flex items-start" data-id="element-90">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 mr-3 flex-shrink-0" data-id="element-91">
                        <ClockIcon size={18} className="text-gray-500" data-id="element-92" />
                      </div>
                      <div className="flex-1" data-id="element-93">
                        <div className="flex justify-between items-start" data-id="element-94">
                          <div data-id="element-95">
                            <span className="font-medium" data-id="element-96">{check.speaker}</span>
                            {check.category && <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs" data-id="element-97">
                                {check.category}
                              </span>}
                          </div>
                          <div className="flex items-center" data-id="element-98">
                            <span className="text-sm text-gray-500 mr-2" data-id="element-99">
                              {formatTime(check.timestamp)}
                            </span>
                            {expandedFactCheck === index ? <ChevronUpIcon size={16} className="text-gray-500" data-id="element-100" /> : <ChevronDownIcon size={16} className="text-gray-500" data-id="element-101" />}
                          </div>
                        </div>
                        <p className="text-gray-700 mt-1 mb-2" data-id="element-102">
                          "{check.claim}"
                        </p>
                        <div className="flex items-center" data-id="element-103">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getRatingBadgeClass(check.rating)}`} data-id="element-104">
                            {getRatingIcon(check.rating)}
                            <span className="ml-1" data-id="element-105">{check.rating}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedFactCheck === index && <div className="px-4 pb-4 pt-2 border-t border-gray-200" data-id="element-106">
                      <div className="ml-13" data-id="element-107">
                        <p className="text-gray-700 mb-3" data-id="element-108">
                          {check.explanation}
                        </p>
                        {renderSources(check.sources)}
                      </div>
                    </div>}
                </div>)}
          </div>}
      </div>
    </div>;
};